window.ShopgatePriceHelper = function (referenceValue) {
  let defaultDecimalDelimiter = ',';
  let defaultSymbolIsSuffix = true;
  let defaultSpaces = 0;
  let defaultCurrencySymbol = '';
  let defaultUseNbsp = true;

  const defaultTaxRate = 19;
  const currencySymbols = {
    EUR: '€',
    GBP: '£',
    USD: '$',
    CAD: 'CAD',
    CHF: 'CHF ',
    RON: 'L',
    PLN: 'zł',
    ARS: 'AP',
    AUD: '$',
  };

  const currenciesWithPrefix = ['GBP', 'USD', 'CHF', 'AUD'];

  /**
   * Checks if a decimal delimiter is valid
   * @param  {string} decimalDelimiter The delimiter
   * @return {string}                  The passed delimiter or the default delimiter
   */
  function checkDecimalDelimiter(decimalDelimiter) {
    const validDelimiters = ['.', ','];

    return validDelimiters
      .indexOf(decimalDelimiter) !== -1 ? decimalDelimiter : defaultDecimalDelimiter;
  }

  /**
   * Returns a matching section delimiter for a decimal delimiter
   * @param  {string} decimalDelimiter The decimal delimiter
   * @param  {string} currencySymbol   A currency symbol
   * @return {string}                  The section delimiter
   */
  function getSectionsDelimiterForDecimalDelimiter(decimalDelimiter, currencySymbol) {
    const specialCurrencyMapping = {
      CHF: '\'',
    };

    let sectionDelimiter = '';

    if (currencySymbol && specialCurrencyMapping.hasOwnProperty(currencySymbol.trim())) {
      sectionDelimiter = specialCurrencyMapping[currencySymbol.trim()];
    } else {
      sectionDelimiter = decimalDelimiter === ',' ? '.' : ',';
    }

    return sectionDelimiter;
  }

  /**
   * Returns the correct decimal delimiter for known currencies
   * @param  {string} currencySymbol The currency symbol
   * @return {string}                The decimal delimiter
   */
  function getDecimalDelimiterForKnownCurrencies(currencySymbol) {
    const currenciesWithDot = ['$', '£', 'CHR'];

    return currenciesWithDot.indexOf(currencySymbol) !== -1 ? '.' : ',';
  }

  /**
   * Replaces regular spaces with non breaking spaces if requested
   * @param {string} value The string that contains the spaces
   * @returns {string} The modified value
   */
  function setCorrectSpaces(value) {
    return defaultUseNbsp ? value.replace(/ /g, '\u00a0') : value;
  }

  function isNumeric(value) {
    return !isNaN(parseFloat(value)) && isFinite(value);
  }

  this.currencyProperties = null;

  this.setDefaultUseNbsp = function (value) {
    defaultUseNbsp = typeof value === 'boolean' ? value : defaultUseNbsp;
  };

  /**
   * Set a default currency symbol
   * @param {string} currencySymbol The currency symbol
   */
  this.setDefaultCurrencySymbol = function (currencySymbol) {
    defaultCurrencySymbol = currencySymbol;
  };

  /**
   * Set defaults like those used by cs function defined in bootstrap.php
   * @param {string} currencyId The currency id
   */
  this.setDefaultsForCurrencyId = function (currencyId) {
    if (!currencySymbols.hasOwnProperty(currencyId)) {
      return;
    }

    defaultCurrencySymbol = currencySymbols[currencyId];
    defaultSymbolIsSuffix = currenciesWithPrefix.indexOf(currencyId) === -1;
    defaultSpaces = defaultSymbolIsSuffix ? 1 : 0;
    defaultDecimalDelimiter = getDecimalDelimiterForKnownCurrencies(defaultCurrencySymbol);
  };

  /**
   * Set a default decimal delimiter
   * @param {string} decimalDelimiter The decimal delimiter
   * @param {string} currencySymbol   A currency symbol
   */
  this.setDefaultDecimalDelimiter = function (decimalDelimiter) {
    defaultDecimalDelimiter = checkDecimalDelimiter(decimalDelimiter);
  };

  /**
   * Determine if the symbol is a suffix for the formatted amounts
   * @param {boolean} isSuffix Currency symbol is a suffix or not
   */
  this.setDefaultCurrencySymbolIsSuffix = function (isSuffix) {
    defaultSymbolIsSuffix = typeof isSuffix === 'boolean' ? isSuffix : true;
  };

  /**
   * Sets the number of spaces between the formatted amount and the currency symbol
   * @param {number} counter The number of spaces
   */
  this.setDefaultSpaces = function (counter) {
    defaultSpaces = !isNaN(Math.abs(parseInt(counter, 10))) ? Math.abs(parseInt(counter, 10)) : 0;
  };

  /**
   * Sets a new reference formatted amount for the money class
   * @param {string} referenceAmount The reference amount
   */
  this.setReferenceFormattedAmount = function (referenceAmount) {
    this.currencyProperties = this.getCurrencyPropertiesFromFormattedAmount(referenceAmount);
  };

  /**
   * Converts a net amount from a gross amount
   * @param  {string|number} amount    The basic amount for the calculation
   * @param  {boolean returnFormatted  If set to TRUE the result will be formatted
   * @param  {number} taxRate          The tax rate for the conversion
   * @return {string|number}           The result
   */
  this.calculateAmountNet = function (amount, taxRate, returnFormatted) {
    amount = this.getAmountUnformatted(amount);
    taxRate = typeof taxRate != 'undefined' ? taxRate : defaultTaxRate;
    taxRate = taxRate < 0 ? 0 : taxRate;

    amount = (amount * 100) / (100 + taxRate);

    return returnFormatted ? this.getAmountFormatted(amount) : amount;
  };

  /**
   * Converts a gross amount from a net amount
   * @param  {string|number} amount    The basic amount for the calculation
   * @param  {boolean returnFormatted  If set to TRUE the result will be formatted
   * @param  {number} taxRate          The tax rate for the conversion
   * @return {string|number}           The result
   */
  this.calculateAmountGross = function (amount, taxRate, returnFormatted) {
    amount = this.getAmountUnformatted(amount);
    taxRate = isNumeric(taxRate) ? taxRate : defaultTaxRate;
    taxRate = taxRate < 0 ? 0 : taxRate;

    amount = (amount * (100 + taxRate)) / 100;

    return returnFormatted ? this.getAmountFormatted(amount) : amount;
  };

  /**
   * Adds or removes a percent value to or from a passed amount
   * @param  {string|number} amount           The amount
   * @param  {number}                         The percent value
   * @param  {boolean} returnFormatted        If set to TRUE the result will be formatted
   * @param  {boolean} returnFormattedZero    If set to TRUE the result will also be formatted if it is 0
   * @return {string|number}                  The saved amount if it is positive or 0
   */
  this.addPercentToAmount = function (amount, percent, returnFormatted, returnFormattedZero) {
    amount = this.getAmountUnformatted(amount);
    amount = (amount * (100 + percent)) / 100;

    if (amount < 1) {
      return returnFormatted && returnFormattedZero ? this.getAmountFormatted(0) : 0;
    } else {
      return returnFormatted ? this.getAmountFormatted(amount) : amount;
    }
  };

  /**
   * Calculates the procentual saving of a new price compared with an old price
   * @param  {string|number} amountOld         The old (higher) price
   * @param  {string|number} amountNew         The new (lower) price
   * @param  {boolean} returnFormatted        If set to TRUE the result will be formatted with a percent sign
   * @param  {boolean} returnFormattedZero    If set to TRUE the result will also be formatted if it is 0
   * @return {string|number}              The saved percent or NULL if nothing is saved
   */
  this.calculatePercentSaved = function (amountOld, amountNew, returnFormatted, returnFormattedZero) {
    var savedPercent = 0;

    amountOld = this.getAmountUnformatted(amountOld);
    amountNew = this.getAmountUnformatted(amountNew);

    savedPercent = Math.round((amountOld - amountNew) / amountOld * 100);

    if (savedPercent < 1) {
      return returnFormatted && returnFormattedZero ? savedPercent + "%" : savedPercent;
    } else {
      return returnFormatted ? savedPercent + "%" : savedPercent;
    }
  };

  /**
   * Calculates the saving of a new price compared with an old price
   * @param  {string|number} amountOld         The old (higher) price
   * @param  {string|number} amountNew         The new (lower) price
   * @param  {boolean} returnFormatted        If set to TRUE the result will be formatted
   * @param  {boolean} returnFormattedZero    If set to TRUE the result will also be formatted if it is 0
   * @return {string|number}                  The saved amount if it is positive or 0 if nothig is saved
   */
  this.calculateAmountSaved = function (amountOld, amountNew, returnFormatted, returnFormattedZero) {
    var savedAmount = this.subtractAmounts(amountOld, amountNew);

    if (savedAmount < 1) {
      return returnFormatted && returnFormattedZero ? this.getAmountFormatted(0) : 0;
    } else {
      return returnFormatted ? this.getAmountFormatted(savedAmount) : savedAmount;
    }
  };

  /**
   * Adds two amounts together
   * @param  {string|number} amountOne The first amount
   * @param  {string|number} amountTwo The second amount
   * @param  {boolean} returnFormatted If set to TRUE the result will be formatted
   * @return {string|number}           The result
   */
  this.addAmounts = function (amountOne, amountTwo, returnFormatted) {
    amountOne = this.getAmountUnformatted(amountOne);
    amountTwo = this.getAmountUnformatted(amountTwo);

    var result = amountOne + amountTwo;

    return returnFormatted ? this.getAmountFormatted(result) : result;
  };

  /**
   * Substracts two amounts
   * @param  {string|number} amountOne The first amount
   * @param  {string|number} amountTwo The second amount
   * @param  {boolean} returnFormatted If set to TRUE the result will be formatted
   * @return {string|number}           The result
   */
  this.subtractAmounts = function (amountOne, amountTwo, returnFormatted) {
    amountOne = this.getAmountUnformatted(amountOne);
    amountTwo = this.getAmountUnformatted(amountTwo);

    var result = amountOne - amountTwo;

    return returnFormatted ? this.getAmountFormatted(result) : result;
  };

  /**
   * Compares two amounts
   * @param  {string|number} amountOne The first amount
   * @param  {string|number} amountTwo The second amount
   * @param  {boolean} returnFormatted        If set to TRUE the result will be formatted if there if a difference between the both amounts
   * @param  {boolean} returnFormattedZero    If set to TRUE the result will also be formatted if both amounts are equal
   * @return {string|number}                  A formatted amount or a nurmeric represantation of the comparison
   */
  this.compareAmounts = function (amountOne, amountTwo, returnFormatted, returnFormattedZero) {
    amountOne = this.getAmountUnformatted(amountOne);
    amountTwo = this.getAmountUnformatted(amountTwo);

    if (amountOne > amountTwo) {
      return this.subtractAmounts(amountTwo, amountOne, returnFormatted);
    } else if (amountOne < amountTwo) {
      return this.subtractAmounts(amountTwo, amountOne, returnFormatted);
    } else {
      return returnFormatted && returnFormattedZero ? this.getAmountFormatted(0) : 0;
    }
  };

  /**
   * Collects currency information for a passed formatted amount of money
   * @param  {string} formattedAmount The formatted amount
   * @return {Object}                 The collected properties for the formatted amount string
   */
  this.getCurrencyPropertiesFromFormattedAmount = function (formattedAmount) {
    /**
     * Creates a regex segment to search for a currency symbol within a formatted amount
     * @param {string} currencySymbol A currency symbol
     * @returns {string} The regex segment
     */
    function getRegexSegmentForCurrencySymbol(currencySymbol) {
      return currencySymbol ? `\\${currencySymbol}` : '';
    }

    const formattedAmountTmp = formattedAmount.trim();

    if (!this.isFormattedAmount(formattedAmountTmp)) {
      return false;
    }

    const result = {
      symbol: formattedAmountTmp.replace(/[\d,.'\s+-]/ig, ''),
      symbol_is_suffix: defaultSymbolIsSuffix,
      spaces: defaultSpaces,
      decimal_delimiter: defaultDecimalDelimiter,
      sections_delimiter: '',
      amount_formatted: '',
      amount: '',
    };

    const symbolIndex = formattedAmountTmp.indexOf(result.symbol);
    const firstDigitIndex = formattedAmountTmp.search(/(\d)/);
    const regexSymbolReplace = new RegExp(`(${getRegexSegmentForCurrencySymbol(result.symbol)}|\\s)`, 'gi');
    const regexSuffixSymbolSpaces = new RegExp(`(\\s*)${getRegexSegmentForCurrencySymbol(result.symbol)}`, 'i');
    const regexPrefixSymbolSpaces = new RegExp(`${getRegexSegmentForCurrencySymbol(result.symbol)}(\\s*)`, 'i');

    let resultRegexSymbolSpaces = null;

    // determine if the currency symbol is a prefix or a suffix in the current currency
    if (symbolIndex < firstDigitIndex) {
      result.symbol_is_suffix = false;
    }

    if (result.symbol_is_suffix) {
      resultRegexSymbolSpaces = formattedAmountTmp.match(regexSuffixSymbolSpaces);
    } else {
      resultRegexSymbolSpaces = formattedAmountTmp.match(regexPrefixSymbolSpaces);
    }

    if (resultRegexSymbolSpaces && resultRegexSymbolSpaces[1]) {
      result.spaces = resultRegexSymbolSpaces[1].length;
    }

    // get the formatted amount from the value string without the currency symbol
    result.amount_formatted = formattedAmountTmp.replace(regexSymbolReplace, '');

    const possibleDecimalDelimiter = result
      .amount_formatted
      .substring(result.amount_formatted.length - 3, result.amount_formatted.length - 2);

    if (possibleDecimalDelimiter === '.' || possibleDecimalDelimiter === ',') {
      result.decimal_delimiter = possibleDecimalDelimiter;
    } else {
      // we didn't find a valid decimal delimiter so we check for a sections delimiter
      const possibleSectionsDelimiter = result
        .amount_formatted
        .substring(result.amount_formatted.length - 4, result.amount_formatted.length - 3);

      if (possibleSectionsDelimiter === '.') {
        result.decimal_delimiter = ',';
      } else if (possibleSectionsDelimiter === ',') {
        result.decimal_delimiter = '.';
      } else {
        result.decimal_delimiter = getDecimalDelimiterForKnownCurrencies(result.symbol);
      }

      result.amount_formatted = `${result.amount_formatted}${result.decimal_delimiter}00`;
    }

    // set the sections delimiter
    result.sections_delimiter = getSectionsDelimiterForDecimalDelimiter(
      result.decimal_delimiter,
      result.symbol
    );

    // get the amount in cent
    result.amount = parseInt(result.amount_formatted.replace(/[,.']/gi, ''), 10);

    return result;
  };

  /**
   * Checks if an amount is formatted or numeric
   * @param  {string|number}  amount The amount
   * @return {boolean}        TRUE if the amount is formatted, FALSE if not
   */
  this.isFormattedAmount = function (amount) {
    let result = false;

    if (typeof amount === 'string') {
      // if the integer representation of the passed string is not equal
      // to the string this could be a formatted amount
      // eslint-disable-next-line eqeqeq Disabled, because done like this on purpose
      result = parseFloat(amount) != amount;

      // the passed string seems to be equal to its integer representation (not formatted)
      // but at some string this comparison may fail "100.00" == 100. so we have so look for
      // characteristics that indicate formatted amounts
      if (!result && amount.search(/[,.'\s]/gi) !== -1) {
        result = true;
      }
    }

    return result;
  };

  /**
   * Formats a numeric amount to a formatted amount
   *
   * @param  {number} amount - The numeric amount
   * @param  {string} [decimalDelimiter] - The desired decimal delimiter (, or .)
   * @param  {string} [currencySymbol] - The desired currency symbol
   * @param  {boolean} [currencySymbolIsSuffix] - Determines if the currency symbol
   *   is a suffix for the formatted amount
   * @param  {number} [spaces] - The number of spaces between the currency symbol and the amount
   * @return {string} - The formatted amount
   */
  this.getAmountFormatted = function (
    amount,
    decimalDelimiter,
    currencySymbol,
    currencySymbolIsSuffix,
    spaces
  ) {
    /* eslint-disable */
    // This function was copied from Stack Overflow, so it wasn't adjusted for the linter
    /**
     * Formats a numeric amount based on some parameters
     * @param integer value: the value that should be formatted
     * @param integer n: length of decimal
     * @param integer x: length of whole part
     * @param mixed   s: sections delimiter
     * @param mixed   c: decimal delimiter
     * @returns string The formatted amount
     */
    function format(value, n, x, s, c) {
      var re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\D' : '$') + ')',
        num = value.toFixed(Math.max(0, ~~n));

      return (c ? num.replace('.', c) : num).replace(new RegExp(re, 'g'), '$&' + (s || ','));
    }
    /* eslint-enable */

    /**
     * Returns a passed number of spaces
     * @param  {number} count The space counter
     * @return {string}       The spaces
     */
    function getSpaces(count) {
      let result = '';

      for (let i = 0; i < count; i += 1) {
        result += ' ';
      }

      return result;
    }

    if (this.isFormattedAmount(amount)) {
      return amount;
    }

    decimalDelimiter = decimalDelimiter ? checkDecimalDelimiter(decimalDelimiter) : (this.currencyProperties && this.currencyProperties.decimal_delimiter ? this.currencyProperties.decimal_delimiter : defaultDecimalDelimiter);
    currencySymbol = currencySymbol ? currencySymbol : (this.currencyProperties && this.currencyProperties.symbol ? this.currencyProperties.symbol : defaultCurrencySymbol);
    currencySymbolIsSuffix = typeof currencySymbolIsSuffix === 'boolean' ? currencySymbolIsSuffix : (this.currencyProperties && typeof this.currencyProperties.symbol_is_suffix === 'boolean' ? this.currencyProperties.symbol_is_suffix : defaultSymbolIsSuffix);
    spaces = typeof spaces === 'number' ? spaces : (this.currencyProperties && typeof this.currencyProperties.spaces === 'number' ? this.currencyProperties.spaces : defaultSpaces);

    const sectionsDelimiter = getSectionsDelimiterForDecimalDelimiter(
      decimalDelimiter,
      currencySymbol
    );

    let formattedAmount = format(amount / 100, 2, 3, sectionsDelimiter, decimalDelimiter);

    // create the formatted amount
    if (currencySymbolIsSuffix) {
      formattedAmount = formattedAmount + getSpaces(spaces) + currencySymbol;
    } else if (amount >= 0) {
      formattedAmount = currencySymbol + getSpaces(spaces) + formattedAmount;
    } else {
      formattedAmount = `-${currencySymbol}${getSpaces(spaces)}${formattedAmount.replace(/-/gi, '')}`;
    }

    return setCorrectSpaces(formattedAmount);
  };

  /**
   * Returns the unformatted representation of a formatted amount
   * @param  {string|number} formattedAmount  The formatted amount
   * @return {number}                         The unformatted amount
   */
  this.getAmountUnformatted = function (formattedAmount) {
    let unformattedAmount = null;

    if (this.isFormattedAmount(formattedAmount)) {
      const tmpProperties = this.getCurrencyPropertiesFromFormattedAmount(formattedAmount);

      if (tmpProperties) {
        unformattedAmount = tmpProperties.amount;
      }
    } else {
      unformattedAmount = formattedAmount;
    }

    return parseInt(unformattedAmount, 10);
  };

  // initialize the object with a reference value if passed
  if (referenceValue) {
    this.setReferenceFormattedAmount(referenceValue);
  }
};



// WEBPACK FOOTER //
// ./Library/1.0/common/shopgate-price-helper.js